(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-cti-section]';

  /*
   * 商品一覧の末端から何px以内で
   * 次の24件を読み込むか。
   *
   * 0に近いほど本当の末端で読み込む。
   */
  const LOAD_TRIGGER_BEFORE_PX = 24;

  /*
   * 高速スクロールで商品一覧末端を少し通過した場合でも
   * 取りこぼさないための許容値。
   *
   * フッターを大きくスクロールした後には
   * 読み込まない。
   */
  const LOAD_TRIGGER_AFTER_PX = 250;


  /*
   * =========================================================
   * JS多重読み込み防止
   * =========================================================
   */

  if (
    window.CollectionTabsInfinite &&
    typeof window.CollectionTabsInfinite.initAll === 'function'
  ) {
    window.CollectionTabsInfinite.initAll(document);
    return;
  }


  const instances = new WeakMap();


  /*
   * =========================================================
   * Section Rendering API URL
   * =========================================================
   */

  function buildSectionUrl(
  collectionUrl,
  page
) {
  const url = new URL(
    collectionUrl,
    window.location.origin
  );

  /*
   * Ajax専用collection template
   */
  url.searchParams.set(
    'view',
    'cti'
  );

  /*
   * paginateページ
   */
  url.searchParams.set(
    'page',
    String(page)
  );

  /*
   * 古いSection Rendering API指定が
   * URLに残るケースを防止
   */
  url.searchParams.delete(
    'section_id'
  );

  return url.toString();
}


  /*
   * =========================================================
   * Ajax追加HTML内のID重複対策
   * =========================================================
   *
   * 同一商品が複数コレクションに所属している場合でも、
   *
   * CardLink-...
   * title-...
   *
   * などのIDが重複しないようにする。
   */

  function scopeFragmentIds(
    container,
    prefix
  ) {
    if (!container) {
      return;
    }


    const safePrefix =
      String(prefix).replace(
        /[^A-Za-z0-9_-]/g,
        '-'
      );


    const idMap =
      new Map();


    /*
     * ---------------------------------
     * IDそのものを書き換える
     * ---------------------------------
     */

    container
      .querySelectorAll('[id]')
      .forEach((element) => {
        const oldId =
          element.id;

        if (!oldId) {
          return;
        }


        const newId =
          `${safePrefix}--${oldId}`;


        idMap.set(
          oldId,
          newId
        );


        element.id =
          newId;
      });


    /*
     * ---------------------------------
     * ID参照属性を書き換える
     * ---------------------------------
     */

    const tokenReferenceAttributes = [
      'for',
      'form',
      'headers',
      'aria-labelledby',
      'aria-describedby',
      'aria-controls',
      'aria-owns'
    ];


    container
      .querySelectorAll('*')
      .forEach((element) => {
        tokenReferenceAttributes.forEach(
          (attributeName) => {
            if (
              !element.hasAttribute(
                attributeName
              )
            ) {
              return;
            }


            const value =
              element.getAttribute(
                attributeName
              );


            if (!value) {
              return;
            }


            const tokens =
              value
                .trim()
                .split(/\s+/)
                .map((token) => {
                  return (
                    idMap.get(token) ||
                    token
                  );
                });


            element.setAttribute(
              attributeName,
              tokens.join(' ')
            );
          }
        );
      });


    /*
     * ---------------------------------
     * href="#xxxx"
     * data-modal="#xxxx"
     * 等を書き換える
     * ---------------------------------
     */

    const hashReferenceAttributes = [
      'href',
      'xlink:href',
      'data-modal',
      'data-target'
    ];


    container
      .querySelectorAll('*')
      .forEach((element) => {
        hashReferenceAttributes.forEach(
          (attributeName) => {
            if (
              !element.hasAttribute(
                attributeName
              )
            ) {
              return;
            }


            const value =
              element.getAttribute(
                attributeName
              );


            if (
              !value ||
              !value.startsWith('#')
            ) {
              return;
            }


            const oldId =
              value.slice(1);


            const newId =
              idMap.get(oldId);


            if (!newId) {
              return;
            }


            element.setAttribute(
              attributeName,
              `#${newId}`
            );
          }
        );
      });
  }


  /*
   * =========================================================
   * Section初期化
   * =========================================================
   */

  function init(root) {
    if (!root) {
      return;
    }


    /*
     * すでに初期化済み
     */

    if (
      instances.has(root)
    ) {
      return;
    }


    const tabs =
      Array.from(
        root.querySelectorAll(
          '[data-cti-tab]'
        )
      );


    const panels =
      Array.from(
        root.querySelectorAll(
          '[data-cti-panel]'
        )
      );


    const sticky =
      root.querySelector(
        '[data-cti-sticky]'
      );


    if (
      tabs.length === 0 ||
      panels.length === 0
    ) {
      return;
    }


    let destroyed =
      false;


    /*
     * scroll処理を
     * requestAnimationFrameで制御
     */

    let scrollRafId =
      null;


    let previousScrollY =
      window.scrollY;


    /*
     * 各panelのAjax通信を管理
     */

    const requestControllers =
      new WeakMap();


    /*
     * =======================================================
     * Tab → Panel取得
     * =======================================================
     */

    function getPanelForTab(
      tab
    ) {
      if (!tab) {
        return null;
      }


      const panelId =
        tab.getAttribute(
          'aria-controls'
        );


      if (!panelId) {
        return null;
      }


      return (
        panels.find(
          (panel) => {
            return (
              panel.id ===
              panelId
            );
          }
        ) ||
        null
      );
    }


    /*
     * =======================================================
     * 現在表示中のPanel
     * =======================================================
     */

    function getActivePanel() {
      return (
        panels.find(
          (panel) => {
            return (
              panel.hidden ===
              false
            );
          }
        ) ||
        null
      );
    }


    /*
     * =======================================================
     * Loading表示
     * =======================================================
     */

    function setLoading(
      panel,
      isLoading
    ) {
      if (!panel) {
        return;
      }


      panel.dataset.loading =
        isLoading
          ? 'true'
          : 'false';


      panel.setAttribute(
        'aria-busy',
        isLoading
          ? 'true'
          : 'false'
      );


      const loading =
        panel.querySelector(
          '[data-cti-loading]'
        );


      if (loading) {
        loading.hidden =
          !isLoading;
      }
    }


    /*
     * =======================================================
     * Status表示
     * =======================================================
     */

    function setStatus(
      panel,
      message = ''
    ) {
      if (!panel) {
        return;
      }


      const status =
        panel.querySelector(
          '[data-cti-status]'
        );


      if (!status) {
        return;
      }


      status.textContent =
        message;
    }


    /*
     * =======================================================
     * Retryボタン
     * =======================================================
     */

    function setRetry(
      panel,
      visible
    ) {
      if (!panel) {
        return;
      }


      const retry =
        panel.querySelector(
          '[data-cti-retry]'
        );


      if (!retry) {
        return;
      }


      retry.hidden =
        !visible;
    }


    /*
     * =======================================================
     * Sentinel表示制御
     * =======================================================
     *
     * 現在はIntersectionObserverには使用しないが、
     * Liquid側との状態表示用として残す。
     */

    function updateSentinel(
      panel
    ) {
      if (!panel) {
        return;
      }


      const sentinel =
        panel.querySelector(
          '[data-cti-sentinel]'
        );


      if (!sentinel) {
        return;
      }


      sentinel.hidden =
        panel.dataset.hasNext !==
        'true';
    }


    /*
     * =======================================================
     * 商品追加完了イベント
     * =======================================================
     *
     * Ajax追加後に
     * U-KOMI・お気に入り等の再初期化が
     * 必要になった場合に使用可能。
     */

    function notifyProductsAdded(
      panel,
      items,
      page
    ) {
      const event =
        new CustomEvent(
          'collection-tabs:products-added',
          {
            bubbles: true,

            detail: {
              root,
              panel,
              items,
              page
            }
          }
        );


      root.dispatchEvent(
        event
      );
    }


    /*
     * =======================================================
     * Panelの通信中断
     * =======================================================
     */

    function abortPanelRequest(
      panel
    ) {
      if (!panel) {
        return;
      }


      const controller =
        requestControllers.get(
          panel
        );


      if (controller) {
        controller.abort();


        requestControllers.delete(
          panel
        );
      }


      /*
       * Abortしたタブを後で再度開いても
       * loading=trueが残らないようにする
       */

      setLoading(
        panel,
        false
      );
    }


    /*
     * =======================================================
     * 非表示タブの通信を中断
     * =======================================================
     */

    function abortInactiveRequests(
      activePanel
    ) {
      panels.forEach(
        (panel) => {
          if (
            panel !==
            activePanel
          ) {
            abortPanelRequest(
              panel
            );
          }
        }
      );
    }


    /*
     * =======================================================
     * 次の24商品取得
     * =======================================================
     */

    async function loadNext(
      panel,
      options = {}
    ) {
      const {
        force = false
      } = options;


      if (
        destroyed ||
        !panel
      ) {
        return;
      }


      /*
       * -----------------------------
       * エラー状態
       * -----------------------------
       *
       * Retryボタンからのみ
       * 再取得を許可
       */

      if (
        panel.dataset.loadError ===
          'true' &&
        !force
      ) {
        return;
      }


      /*
       * -----------------------------
       * すでに通信中
       * -----------------------------
       */

      if (
        panel.dataset.loading ===
        'true'
      ) {
        return;
      }


      /*
       * -----------------------------
       * 次ページなし
       * -----------------------------
       */

      if (
        panel.dataset.hasNext !==
        'true'
      ) {
        return;
      }


      const collectionUrl =
        panel.dataset.collectionUrl;


      if (!collectionUrl) {
        console.error(
          '[CollectionTabsInfinite]',
          'data-collection-url is missing.',
          panel
        );

        return;
      }


      const currentPage =
        Number(
          panel.dataset.page ||
          0
        );


      const nextPage =
        currentPage + 1;


      /*
       * Retryの場合
       */

      if (force) {
        panel.dataset.loadError =
          'false';
      }


      /*
       * -----------------------------
       * AbortController
       * -----------------------------
       */

      const controller =
        new AbortController();


      requestControllers.set(
        panel,
        controller
      );


      setLoading(
        panel,
        true
      );


      setRetry(
        panel,
        false
      );


      setStatus(
        panel,
        ''
      );


      try {
        /*
         * -----------------------------
         * URL生成
         * -----------------------------
         */

        const requestUrl =
  buildSectionUrl(
    collectionUrl,
    nextPage
  );


        console.debug(
          '[CollectionTabsInfinite] loading:',
          requestUrl
        );


        /*
         * -----------------------------
         * Shopify Section Rendering API
         * -----------------------------
         */

        const response =
          await fetch(
            requestUrl,
            {
              method: 'GET',

              credentials:
                'same-origin',

              signal:
                controller.signal,

              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );


        if (!response.ok) {
          throw new Error(
            `Section Rendering API error: ${response.status} ${response.statusText}`
          );
        }


        /*
         * -----------------------------
         * HTML取得
         * -----------------------------
         */

        const html =
          await response.text();


        if (!html.trim()) {
          throw new Error(
            'Section Rendering API returned empty HTML.'
          );
        }


        /*
         * Abort済みなら終了
         */

        if (
          controller.signal.aborted
        ) {
          return;
        }


        /*
         * -----------------------------
         * HTML Parse
         * -----------------------------
         */

        const parser =
          new DOMParser();


        const responseDocument =
          parser.parseFromString(
            html,
            'text/html'
          );


        const responseRoot =
          responseDocument.querySelector(
            '[data-cti-ajax-response]'
          );


        if (!responseRoot) {
          console.error(
            '[CollectionTabsInfinite] unexpected response:',
            html.slice(
              0,
              1000
            )
          );


          throw new Error(
            'data-cti-ajax-response was not found.'
          );
        }


        const ajaxItems =
          responseRoot.querySelector(
            '[data-cti-ajax-items]'
          );


        if (!ajaxItems) {
          throw new Error(
            'data-cti-ajax-items was not found.'
          );
        }


        const grid =
          panel.querySelector(
            '[data-cti-grid]'
          );


        if (!grid) {
          throw new Error(
            'data-cti-grid was not found.'
          );
        }


        /*
         * -----------------------------
         * Ajax HTML内IDをユニーク化
         * -----------------------------
         */

        scopeFragmentIds(
          ajaxItems,
          `${panel.id}-page-${nextPage}`
        );


        /*
         * -----------------------------
         * 商品取得
         * -----------------------------
         */

        const newItems =
          Array.from(
            ajaxItems.children
          );


        /*
         * -----------------------------
         * DOMへ一括追加
         * -----------------------------
         */

        const fragment =
          document.createDocumentFragment();


        newItems.forEach(
          (item) => {
            fragment.appendChild(
              item
            );
          }
        );


        grid.appendChild(
          fragment
        );


        /*
         * -----------------------------
         * Pagination状態
         * -----------------------------
         */

        const returnedPage =
          Number(
            responseRoot.dataset
              .currentPage ||
            nextPage
          );


        const hasNext =
          responseRoot.dataset
            .hasNext ===
          'true';


        panel.dataset.page =
          String(
            returnedPage
          );


        panel.dataset.hasNext =
          hasNext
            ? 'true'
            : 'false';


        panel.dataset.loaded =
          'true';


        panel.dataset.loadError =
          'false';


        /*
         * -----------------------------
         * 商品0件
         * -----------------------------
         */

        if (
          returnedPage === 1 &&
          newItems.length === 0
        ) {
          setStatus(
            panel,
            '商品がありません。'
          );
        }


        /*
         * -----------------------------
         * Sentinel更新
         * -----------------------------
         */

        updateSentinel(
          panel
        );


        /*
         * -----------------------------
         * 外部JSへ通知
         * -----------------------------
         */

        if (
          newItems.length > 0
        ) {
          notifyProductsAdded(
            panel,
            newItems,
            returnedPage
          );
        }


        console.debug(
          '[CollectionTabsInfinite] loaded:',
          {
            page:
              returnedPage,

            items:
              newItems.length,

            hasNext:
              hasNext
          }
        );


        /*
         * ===================================================
         * 重要
         * ===================================================
         *
         * ここでは次ページを絶対に読み込まない。
         *
         * loadNext()
         * checkActivePanelEnd()
         *
         * のどちらも呼ばない。
         *
         * 次の24件は、
         * ユーザーが再度実際にスクロールした時だけ
         * 読み込まれる。
         */


      } catch (error) {
        /*
         * -----------------------------
         * タブ切替によるAbort
         * -----------------------------
         *
         * 正常動作なので
         * エラー表示しない。
         */

        if (
          error.name ===
          'AbortError'
        ) {
          console.debug(
            '[CollectionTabsInfinite] request aborted.'
          );

          return;
        }


        /*
         * -----------------------------
         * 通常エラー
         * -----------------------------
         */

        console.error(
          '[CollectionTabsInfinite]',
          error
        );


        panel.dataset.loadError =
          'true';


        setStatus(
          panel,
          '商品の読み込みに失敗しました。'
        );


        setRetry(
          panel,
          true
        );


      } finally {
        /*
         * 現在panelに登録されているControllerが
         * 今回の通信と同じか確認する。
         *
         * 高速タブ切替で古い通信が終了した際に、
         * 新しい通信のloading状態を
         * falseにしてしまうのを防止。
         */

        const currentController =
          requestControllers.get(
            panel
          );


        if (
          currentController ===
          controller
        ) {
          requestControllers.delete(
            panel
          );


          setLoading(
            panel,
            false
          );
        }
      }
    }


    /*
     * =======================================================
     * 商品一覧末端判定
     * =======================================================
     *
     * window全体ではなく、
     * 現在表示している商品のgridの末端を見る。
     *
     * その下にFooterが存在していても問題ない。
     */

    function checkActivePanelEnd() {
      if (destroyed) {
        return;
      }


      const panel =
        getActivePanel();


      if (!panel) {
        return;
      }


      /*
       * -----------------------------
       * 次ページなし
       * -----------------------------
       */

      if (
        panel.dataset.hasNext !==
        'true'
      ) {
        return;
      }


      /*
       * -----------------------------
       * 通信中
       * -----------------------------
       */

      if (
        panel.dataset.loading ===
        'true'
      ) {
        return;
      }


      /*
       * -----------------------------
       * エラー中
       * -----------------------------
       */

      if (
        panel.dataset.loadError ===
        'true'
      ) {
        return;
      }


      const grid =
        panel.querySelector(
          '[data-cti-grid]'
        );


      if (!grid) {
        return;
      }


      /*
       * 商品がまだ存在しないタブは
       * activateTab()で1ページ目を取得するので
       * スクロール側では何もしない。
       */

      if (
        grid.children.length === 0
      ) {
        return;
      }


      /*
       * -----------------------------
       * Grid位置
       * -----------------------------
       */

      const rect =
        grid.getBoundingClientRect();


      const viewportHeight =
        window.innerHeight ||
        document.documentElement
          .clientHeight;


      /*
       * viewport下端を0として、
       * grid末端がどこにあるか。
       *
       * 例:
       *
       * 500px
       * → 商品末端までまだ500px
       *
       * 20px
       * → もうすぐ商品末端
       *
       * 0px
       * → 商品末端が画面下端
       *
       * -50px
       * → 50pxだけ通過した
       */

      const distanceFromBottom =
        rect.bottom -
        viewportHeight;


      /*
       * =====================================================
       * 読み込み条件
       * =====================================================
       *
       * 商品一覧末端24px手前～
       * 末端通過250pxまで。
       *
       * 高速スクロールによる取りこぼしを防ぐが、
       * Footerを大きくスクロールした後には
       * ロードしない。
       */

      const reachedBottom =
        (
          distanceFromBottom <=
          LOAD_TRIGGER_BEFORE_PX
        ) &&
        (
          distanceFromBottom >=
          -LOAD_TRIGGER_AFTER_PX
        );


      if (!reachedBottom) {
        return;
      }


      loadNext(
        panel
      );
    }


    /*
     * =======================================================
     * Window Scroll
     * =======================================================
     *
     * scrollイベントごとに
     * getBoundingClientRect()を直接実行しない。
     *
     * requestAnimationFrameで
     * 最大1フレーム1回に制限。
     */

    function handleWindowScroll() {
      if (destroyed) {
        return;
      }


      const currentScrollY =
        window.scrollY;


      /*
       * 下方向へスクロールした時だけ
       * Infinite Scrollを判定。
       */

      const scrollingDown =
        currentScrollY >
        previousScrollY;


      previousScrollY =
        currentScrollY;


      if (!scrollingDown) {
        return;
      }


      /*
       * すでに次フレーム判定予約済み
       */

      if (
        scrollRafId !==
        null
      ) {
        return;
      }


      scrollRafId =
        window.requestAnimationFrame(
          () => {
            scrollRafId =
              null;


            checkActivePanelEnd();
          }
        );
    }


    /*
     * =======================================================
     * タブを横スクロール領域内へ表示
     * =======================================================
     */

    function ensureTabVisible(
      tab
    ) {
      if (!tab) {
        return;
      }


      const scroller =
        tab.closest(
          '[data-cti-tab-scroller]'
        );


      if (!scroller) {
        return;
      }


      const tabRect =
        tab.getBoundingClientRect();


      const scrollerRect =
        scroller.getBoundingClientRect();


      const leftOverflow =
        tabRect.left <
        scrollerRect.left;


      const rightOverflow =
        tabRect.right >
        scrollerRect.right;


      if (
        !leftOverflow &&
        !rightOverflow
      ) {
        return;
      }


      const targetLeft =
        tab.offsetLeft -
        scroller.clientWidth / 2 +
        tab.offsetWidth / 2;


      scroller.scrollTo({
        left:
          Math.max(
            0,
            targetLeft
          ),

        behavior:
          'smooth'
      });
    }


    /*
     * =======================================================
     * タブ変更時の商品先頭位置補正
     * =======================================================
     *
     * 例えばタブAで商品100件目まで進んだ状態から
     * タブBへ変更すると、
     * タブBの商品数が少なくページ高さが急に縮む。
     *
     * その場合、商品一覧の先頭付近へ戻す。
     */

    function alignPanelAfterSwitch(
      panel
    ) {
      if (
        !panel ||
        !sticky
      ) {
        return;
      }


      const stickyStyle =
        window.getComputedStyle(
          sticky
        );


      const stickyTop =
        Number.parseFloat(
          stickyStyle.top
        ) ||
        0;


      const panelTop =
        panel
          .getBoundingClientRect()
          .top +
        window.scrollY;


      const targetTop =
        Math.max(
          0,

          panelTop -
          sticky.offsetHeight -
          stickyTop -
          8
        );


      /*
       * 現在位置が商品一覧より
       * 明らかに下の場合だけ戻す。
       */

      if (
        window.scrollY >
        targetTop + 20
      ) {
        window.scrollTo({
          top:
            targetTop,

          behavior:
            'auto'
        });


        /*
         * programmatic scroll後の
         * scroll方向判定を正常化
         */

        previousScrollY =
          window.scrollY;
      }
    }


    /*
     * =======================================================
     * Tab切替
     * =======================================================
     */

    function activateTab(
      activeTab,
      options = {}
    ) {
      const {
        focus = false,
        userInitiated = false
      } = options;


      if (
        !activeTab ||
        destroyed
      ) {
        return;
      }


      const activePanel =
        getPanelForTab(
          activeTab
        );


      if (!activePanel) {
        return;
      }


      /*
       * =====================================================
       * まずUIを即座に切り替える
       * =====================================================
       *
       * fetch等より先に行う。
       */


      /*
       * -----------------------------
       * Tab状態
       * -----------------------------
       */

      tabs.forEach(
        (tab) => {
          const isActive =
            tab ===
            activeTab;


          tab.classList.toggle(
            'is-active',
            isActive
          );


          tab.setAttribute(
            'aria-selected',

            isActive
              ? 'true'
              : 'false'
          );


          tab.tabIndex =
            isActive
              ? 0
              : -1;
        }
      );


      /*
       * -----------------------------
       * Panel表示
       * -----------------------------
       */

      panels.forEach(
        (panel) => {
          panel.hidden =
            panel !==
            activePanel;
        }
      );


      /*
       * =====================================================
       * ここまででタブUIは切替済み
       * =====================================================
       */


      /*
       * 非表示タブのAjaxは停止
       */

      abortInactiveRequests(
        activePanel
      );


      /*
       * タブ横位置
       */

      ensureTabVisible(
        activeTab
      );


      /*
       * 商品一覧縦位置
       */

      if (userInitiated) {
        alignPanelAfterSwitch(
          activePanel
        );
      }


      /*
       * scroll方向判定を更新
       */

      previousScrollY =
        window.scrollY;


      /*
       * -----------------------------
       * 現在ロード済みページ
       * -----------------------------
       */

      const currentPage =
        Number(
          activePanel.dataset.page ||
          0
        );


      /*
       * =====================================================
       * 未ロードタブ
       * =====================================================
       *
       * data-page="0"
       *
       * タブを押した時だけ
       * 最初の24商品を取得。
       */

      if (
        currentPage === 0 &&
        activePanel.dataset.hasNext ===
          'true'
      ) {
        /*
         * UI切替を先にブラウザへ反映させるため
         * 次フレームでAjax開始。
         */

        window.requestAnimationFrame(
          () => {
            /*
             * 次フレーム時点ですでに
             * 別タブへ移動していたら取得しない。
             */

            if (
              activePanel.hidden ||
              destroyed
            ) {
              return;
            }


            loadNext(
              activePanel
            );
          }
        );
      }


      /*
       * =====================================================
       * 既に1ページ以上ロード済みのタブ
       * =====================================================
       *
       * 何もしない。
       *
       * 次ページはユーザーが下方向に
       * スクロールした時だけ取得する。
       */


      /*
       * -----------------------------
       * Keyboard focus
       * -----------------------------
       */

      if (focus) {
        try {
          activeTab.focus({
            preventScroll: true
          });

        } catch (error) {
          activeTab.focus();
        }
      }
    }


    /*
     * =======================================================
     * Tab Click
     * =======================================================
     */

    tabs.forEach(
      (tab) => {
        tab.addEventListener(
          'click',
          () => {
            activateTab(
              tab,
              {
                focus: false,
                userInitiated: true
              }
            );
          }
        );
      }
    );


    /*
     * =======================================================
     * Keyboard navigation
     * =======================================================
     */

    tabs.forEach(
      (tab, index) => {
        tab.addEventListener(
          'keydown',
          (event) => {
            let nextIndex =
              null;


            switch (
              event.key
            ) {
              case 'ArrowRight':
                nextIndex =
                  (index + 1) %
                  tabs.length;

                break;


              case 'ArrowLeft':
                nextIndex =
                  (
                    index -
                    1 +
                    tabs.length
                  ) %
                  tabs.length;

                break;


              case 'Home':
                nextIndex =
                  0;

                break;


              case 'End':
                nextIndex =
                  tabs.length -
                  1;

                break;


              default:
                return;
            }


            event.preventDefault();


            activateTab(
              tabs[nextIndex],
              {
                focus: true,
                userInitiated: true
              }
            );
          }
        );
      }
    );


    /*
     * =======================================================
     * Retry
     * =======================================================
     */

    panels.forEach(
      (panel) => {
        const retry =
          panel.querySelector(
            '[data-cti-retry]'
          );


        if (!retry) {
          return;
        }


        retry.addEventListener(
          'click',
          () => {
            loadNext(
              panel,
              {
                force: true
              }
            );
          }
        );
      }
    );


    /*
     * =======================================================
     * Scroll Listener
     * =======================================================
     */

    window.addEventListener(
      'scroll',
      handleWindowScroll,
      {
        passive: true
      }
    );


    /*
     * =======================================================
     * Destroy
     * =======================================================
     */

    function destroy() {
      destroyed =
        true;


      /*
       * Scroll解除
       */

      window.removeEventListener(
        'scroll',
        handleWindowScroll
      );


      /*
       * requestAnimationFrame解除
       */

      if (
        scrollRafId !==
        null
      ) {
        window.cancelAnimationFrame(
          scrollRafId
        );


        scrollRafId =
          null;
      }


      /*
       * 全Ajax停止
       */

      panels.forEach(
        (panel) => {
          abortPanelRequest(
            panel
          );
        }
      );


      instances.delete(
        root
      );
    }


    /*
     * =======================================================
     * Instance
     * =======================================================
     */

    const instance = {
      root,
      tabs,
      panels,
      activateTab,
      getActivePanel,
      loadNext,
      checkActivePanelEnd,
      destroy
    };


    instances.set(
      root,
      instance
    );


    return instance;
  }


  /*
   * =========================================================
   * 全Section初期化
   * =========================================================
   */

  function initAll(
    scope = document
  ) {
    if (!scope) {
      return;
    }


    /*
     * scope自身がsection
     */

    if (
      scope.matches &&
      scope.matches(
        ROOT_SELECTOR
      )
    ) {
      init(
        scope
      );
    }


    /*
     * scope配下
     */

    if (
      scope.querySelectorAll
    ) {
      scope
        .querySelectorAll(
          ROOT_SELECTOR
        )
        .forEach(
          (root) => {
            init(
              root
            );
          }
        );
    }
  }


  /*
   * =========================================================
   * Global API
   * =========================================================
   */

  window.CollectionTabsInfinite = {
    initAll,

    getInstance(root) {
      return (
        instances.get(root) ||
        null
      );
    }
  };


  /*
   * =========================================================
   * Initial Load
   * =========================================================
   */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll(
          document
        );
      },
      {
        once: true
      }
    );

  } else {
    initAll(
      document
    );
  }


  /*
   * =========================================================
   * Shopify Theme Editor
   * =========================================================
   */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(
        event.target
      );
    }
  );


  /*
   * =========================================================
   * Theme Editor Section Unload
   * =========================================================
   */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        (
          event.target.matches &&
          event.target.matches(
            ROOT_SELECTOR
          )
        )
          ? event.target
          : (
              event.target
                .querySelector
                ? event.target
                    .querySelector(
                      ROOT_SELECTOR
                    )
                : null
            );


      if (!root) {
        return;
      }


      const instance =
        instances.get(
          root
        );


      if (instance) {
        instance.destroy();
      }
    }
  );


  /*
   * =========================================================
   * Theme Editor Block Select
   * =========================================================
   *
   * Shopifyカスタマイザーで
   * コレクションblockをクリックした場合、
   * そのタブを表示する。
   */

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      let tab =
        null;


      if (
        event.target.matches &&
        event.target.matches(
          '[data-cti-tab]'
        )
      ) {
        tab =
          event.target;

      } else if (
        event.target.querySelector
      ) {
        tab =
          event.target.querySelector(
            '[data-cti-tab]'
          );
      }


      if (!tab) {
        return;
      }


      const root =
        tab.closest(
          ROOT_SELECTOR
        );


      if (!root) {
        return;
      }


      const instance =
        instances.get(
          root
        );


      if (!instance) {
        return;
      }


      instance.activateTab(
        tab,
        {
          focus: false,
          userInitiated: false
        }
      );
    }
  );
})();